import { useContext, useState, useCallback, useMemo, useEffect, useRef, type ReactNode } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import { type SingleValue, Select, type OptionType } from "ui/select";
import { useDisclosure } from "hooks/use-disclosure";

import { Box } from "ui/box";
import { Button } from "ui/button";
import VersionEditModal, {
  FormType,
  SubmitFormProps,
} from "components/modals/version-edit-modal/version-edit-modal";
import ConfirmArchiveModal from "components/modals/confirm-archive-modal/confirm-archive-modal";
import ConfirmUpdateMergeTagsModal from "components/modals/confirm-update-merge-tags-modal/confirm-update-merge-tags-modal";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import toast from "components/partials/toast/toast";
import { CloneTouchpointModal } from "components/modals/clone-touchpoint-modal/clone-touchpoint-modal";
import { CreateParentTouchpointModal } from "components/modals/create-parent-touchpoint-modal/create-parent-touchpoint-modal";
import { ApproveAsClientModal } from "components/modals/approve-as-client-modal/approve-as-client-modal";
import { RejectAsClientModal } from "components/modals/reject-as-client-modal/reject-as-client-modal";
import UpdateClientThemeModal from "components/modals/update-client-theme-modal/update-client-theme-modal";
import CompareVersionsModal from "components/modals/compare-versions-modal/compare-versions-modal";
import CreateTouchpointFromProgramTemplateModal from "components/modals/create-touchpoint-from-program-template-modal/create-touchpoint-from-program-template-modal";
import EditProgramTemplateNameModal from "components/modals/edit-program-template-name-modal/edit-program-template-name-modal";
import CloneProgramTemplatesModal from "components/modals/clone-program-templates-modal/clone-program-templates-modal";
import RefineEmailContentModal from "components/modals/refine-email-content-modal/refine-email-content-modal";
import { useRefineEmailContent } from "components/modals/refine-email-content-modal/use-refine-email-content";
import { TouchpointApproveButton } from "containers/admin/clients/touchpoint/components/touchpoint-approve-send-feedback/touchpoint-approve-button";
import { TouchpointSendFeedbackButton } from "containers/admin/clients/touchpoint/components/touchpoint-approve-send-feedback/touchpoint-send-feedback-button";
import ClientReviewStatusIndicator from "containers/admin/clients/touchpoint/components/client-review-status-indicator/client-review-status-indicator";
import ThemeStatusIndicator from "containers/admin/clients/touchpoint/components/theme-status-indicator/theme-status-indicator";
import PreviewTabHeaderMenu from "containers/admin/clients/touchpoint/tab-components/preview-tab/components/preview-tab-header/preview-tab-header-menu/preview-tab-header-menu";
import DisabledActionTooltip from "containers/admin/clients/touchpoint/tab-components/preview-tab/components/preview-tab-header/disabled-action-tooltip/disabled-action-tooltip";
import { getTouchpointActionGates } from "containers/admin/clients/touchpoint/tab-components/preview-tab/components/preview-tab-header/get-touchpoint-action-gates";
import { getRewriteStrategyLinks } from "containers/admin/clients/campaign/rewrite-strategy-links";
import type { DisplayedProgramTemplate } from "containers/admin/clients/client/program-templates-page/components/program-templates-table/program-templates-table.helper";
import ControlBar from "components/partials/control-bar/control-bar";
import { Tooltip } from "ui/tooltip";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";
import { TOUCHPOINT_PREVIEW_VERSION_ID_QUERY } from "utilities/app-routes";
import { IconButton } from "ui/icon-button";
import { useHandleEditorOpen } from "containers/admin/clients/touchpoint/components/builder/use-handle-editor-open";

import { useGetTouchpointVersionStatusesQuery } from "hooks/api/dictionary";

import CommentContext from "contexts/comment-context";
import { useFeatureFlag } from "contexts/feature-flag-context";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useCurrentUser } from "hooks/api/use-current-user";
import { useBuilderAccess } from "containers/admin/clients/touchpoint/components/builder/use-builder-access";
import {
  useCreateDraftVersion,
  useEditTouchpointVersion,
  usePatchTouchpointVersionStatus,
  useSetTouchpointVersionCache,
  useTouchpointVersions,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";
import { useUpdateMergeTags } from "containers/admin/clients/touchpoint/hooks/use-update-merge-tags";

import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { mapTouchpointVersionDTO } from "utilities/api-mappers";

const touchpointVersionApi = getTouchpointVersion();

import { getActionTouchpointTypeLabel, getDisplayTouchpointType, getErrorMessage, getTouchpointType, isLocked } from "utilities";
import { hasPermission } from "utilities/user";
import {
  canApproveSendFeedbackOnTouchpoint,
  isVersionEditable,
  getIsApproved,
  getUserCanPublish,
  getSuccessMessage,
  getFailMessage,
  getClientPortalVersionSelectLabel,
  getShouldShowClientUpdatesInProgressBadge,
} from "utilities/touchpoint";


import { TouchpointStatuses, DEFAULT_CLIENT_REVIEW_STATUSES, type BuilderContent } from "types";
import { CreatableTouchpointType, TouchpointType } from "types/touchpoint";
import { Permission } from "types/auth";
import Icons from "assets";
import { Chip } from "ui/chip";

type CreateTouchpointFromTemplateType = CreatableTouchpointType | null;

/** `large` is the 40px box that pairs with the select. */
const COMPARE_VERSIONS_BUTTON_SX: SxProps<Theme> = { ml: 1 };

type VersionOption = OptionType & { badge: ReactNode };

/** The legacy `w-80`: 20rem. */
const VERSION_SELECT_SX: SxProps<Theme> = { width: 320 };

const VERSION_OPTION_SX: SxProps<Theme> = {
  display: "flex",
  flex: 1,
  alignItems: "center",
  gap: 0.5,
  minWidth: 0,
};

const VERSION_OPTION_LABEL_SX: SxProps<Theme> = { minWidth: 0, overflowWrap: "break-word" };

const TEMPLATE_PENDING_TOOLTIP_SX: SxProps<Theme> = { textAlign: "center" };

const VERSION_OPTION_BADGE_SX: SxProps<Theme> = {
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
};

/** The list puts a version's badges after its name, where the closed control's `endIcon` shows them. */
const renderVersionOption = ({ label, badge }: VersionOption) => (
  <Box component="span" sx={VERSION_OPTION_SX}>
    <Box component="span" sx={VERSION_OPTION_LABEL_SX}>
      {label}
    </Box>
    {badge && (
      <Box component="span" sx={VERSION_OPTION_BADGE_SX}>
        {badge}
      </Box>
    )}
  </Box>
);

const TEMPLATE_APPROVAL_BLOCKED_STATUSES: string[] = [
  TouchpointStatuses.APPROVED,
  TouchpointStatuses.REJECTED,
  TouchpointStatuses.ARCHIVED,
  TouchpointStatuses.ARCHIVED_DRAFT,
  TouchpointStatuses.ARCHIVED_CLIENT_REVIEW,
  TouchpointStatuses.PUBLISHED,
];

const isTemplatePendingApproval = (status?: string): boolean =>
  !!status && !TEMPLATE_APPROVAL_BLOCKED_STATUSES.includes(status);

interface TouchpointPreviewTabHeaderProps {
  selectedVersion?: TouchpointVersionAttributes;
  setSelectedVersion: (version: TouchpointVersionAttributes) => void;
  setIsAddToBatchModalOpen: (isOpen: boolean) => void;
}

export const TouchpointPreviewTabHeader = ({
  selectedVersion,
  setSelectedVersion,
  setIsAddToBatchModalOpen,
}: TouchpointPreviewTabHeaderProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const previewVersionIdFromUrlRef = useRef<string | null>(null);
  previewVersionIdFromUrlRef.current = searchParams.get(TOUCHPOINT_PREVIEW_VERSION_ID_QUERY);
  const { accessBuilder } = useBuilderAccess();
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const currentUser = useCurrentUser();
  const isBatchManagementEnabled = useFeatureFlag("batch-management");
  const versionScope = {
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  };
  const { data: touchpointVersions, refetch: refetchTouchpointVersions } =
    useTouchpointVersions(versionScope);
  const setTouchpointVersionCache = useSetTouchpointVersionCache(versionScope);
  const patchStatusMutation = usePatchTouchpointVersionStatus(versionScope);
  const editVersionMutation = useEditTouchpointVersion(versionScope);
  const createDraftMutation = useCreateDraftVersion(versionScope);
  const { updateMergeTags, isUpdating: isUpdatingMergeTags } = useUpdateMergeTags(versionScope);
  const { data: touchpointVersionStatuses = [] } = useGetTouchpointVersionStatusesQuery();
  const { handleEditorOpen } = useHandleEditorOpen({
    selectedVersion,
    latestVersion: selectedVersion,
  });

  const versions = touchpointVersions?.items ?? [];

  const getClientVersionStatuses = (status?: string) => {
    if (!status) {
      return DEFAULT_CLIENT_REVIEW_STATUSES;
    }

    return (
      touchpointVersionStatuses.find((el) => el.status === status) || DEFAULT_CLIENT_REVIEW_STATUSES
    );
  };

  const editVersionModal = useDisclosure();
  const newDraftModal = useDisclosure();
  const confirmPublishModal = useDisclosure();
  const confirmArchiveModal = useDisclosure();
  const confirmUpdateMergeTagsModal = useDisclosure();
  const cloneTouchpointModal = useDisclosure();
  const [isCloneTemplateOpen, setIsCloneTemplateOpen] = useState(false);
  const [isEditProgramTemplateNameModalOpen, setIsEditProgramTemplateNameModalOpen] =
    useState(false);
  const [isCreateParentTouchpointModalOpen, setIsCreateParentTouchpointModalOpen] = useState(false);
  const approveAsClientModal = useDisclosure();
  const [isRejectAsClientModalOpen, setIsRejectAsClientModalOpen] = useState(false);
  const [isUpdateClientThemeModalOpen, setIsUpdateClientThemeModalOpen] = useState(false);
  const [isRefineEmailContentModalOpen, setIsRefineEmailContentModalOpen] = useState(false);
  const [createTouchpointFromTemplateType, setCreateTouchpointFromTemplateType] =
    useState<CreateTouchpointFromTemplateType>(null);

  const { refineEmail, isSubmitting: isRefineEmailSubmitting } = useRefineEmailContent({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
    versionId: selectedVersion?.id ?? "",
    currentTouchpoint,
    onSuccess: () => setIsRefineEmailContentModalOpen(false),
  });

  const { commentsSummary } = useContext(CommentContext);
  const userCanWrite = hasPermission(currentUser, Permission.PERM_TOUCHPOINT_WRITE);
  const hasClientPortalAccess = hasPermission(currentUser, Permission.PERM_CLIENT_USER);
  const isClientAdmin =
    hasClientPortalAccess && hasPermission(currentUser, Permission.PERM_CLIENT_APPROVAL);
  const isClientUser = hasClientPortalAccess && !isClientAdmin;

  const showClientUpdatesInProgressBadge = useMemo(
    () =>
      hasClientPortalAccess && getShouldShowClientUpdatesInProgressBadge(versions, selectedVersion),
    [hasClientPortalAccess, versions, selectedVersion],
  );

  const selectedVersionIsPublished = selectedVersion?.status === TouchpointStatuses.PUBLISHED;
  const touchpointIsEmail = selectedVersion?.type === TouchpointType.EMAIL;
  const touchpointIsOther = selectedVersion?.type === TouchpointType.OTHER;
  const isTemplate = currentCampaign.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME;
  const rewriteStrategyLinks = getRewriteStrategyLinks(currentClient.id, currentCampaign);

  const [isSendBtnLoading, setIsSendBtnLoading] = useState<boolean>(false);
  const [approvePublishLoading, setApprovePublishLoading] = useState<boolean>(false);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState<boolean>(false);
  const [isCompareVersionsModalOpen, setIsCompareVersionsModalOpen] = useState(false);

  const [isLockedByAnotherUser, setIsLockedByAnotherUser] = useState(
    selectedVersion ? isLocked(selectedVersion as unknown as BuilderContent, currentUser) : false,
  );

  const verifyLockAndProceed = useCallback(
    async (callback: () => void) => {
      try {
        if (!selectedVersion) return;

        // Get latest version data
        const freshVersionDto = await touchpointVersionApi.getOneVersion({
          clientId: currentClient?.id,
          campaignId: currentCampaign?.id,
          touchpointId: currentTouchpoint?.id,
          id: selectedVersion.id,
        });
        const freshVersion = mapTouchpointVersionDTO(freshVersionDto);

        // Check if locked using the utility
        const isLockedByAnother = isLocked(freshVersion as unknown as BuilderContent, currentUser);

        // Update state to reflect current lock status
        setIsLockedByAnotherUser(isLockedByAnother);

        setTouchpointVersionCache(freshVersion);

        // If this is the currently selected version, update our local state too
        if (selectedVersion.id === freshVersion.id) {
          setSelectedVersion(freshVersion);
        }

        if (isLockedByAnother) {
          toast.warning({
            data: {
              title: "Action not permitted",
              message: `This version is currently locked by ${freshVersion.lockedBy?.firstName} ${freshVersion.lockedBy?.lastName}`,
            },
          });
        } else {
          // Not locked, proceed with the action
          callback();
        }
      } catch (error) {
        console.error("Error verifying lock status:", error);
        toast.error({
          data: {
            title: "Failed to verify lock status",
          },
        });
      }
    },
    [
      selectedVersion,
      currentClient,
      currentCampaign,
      currentTouchpoint,
      currentUser,
      setTouchpointVersionCache,
      setSelectedVersion,
    ],
  );

  const handleEditEmail = () => {
    accessBuilder(selectedVersion);
  };

  const handleCustomSelectChange = (selectedOption: unknown) => {
    const opt = selectedOption as SingleValue<OptionType>;
    const newSelectedTouchpoint = versions.find((version) => version.id === opt?.value);

    if (newSelectedTouchpoint) {
      setSelectedVersion(newSelectedTouchpoint);
    }
  };

  const selectOptions = versions.map(({ id, status, version, aiGenerated }) => {
    const statusDetails = getClientVersionStatuses(status);
    const versionStatusLabel = hasClientPortalAccess
      ? getClientPortalVersionSelectLabel(statusDetails, status)
      : statusDetails?.versionStatusName;
    const aiIcon = aiGenerated && !hasClientPortalAccess ? <Chip size="small" color="info" variant="filled" label="Apollo drafted" /> : null;
    const infoIcon =
      isTemplate && !hasClientPortalAccess && isTemplatePendingApproval(status) ? (
        <Tooltip
          label={`This template must be approved before creating ${getActionTouchpointTypeLabel(currentTouchpoint.type).toLowerCase()}`}
          placement="bottom"
          width={192}
          sx={TEMPLATE_PENDING_TOOLTIP_SX}>
          <Icons.InfoIndicator className="size-5 text-gray-600" />
        </Tooltip>
      ) : null;
    const badge =
      aiIcon || infoIcon ? (
        <>
          {aiIcon}
          {infoIcon}
        </>
      ) : null;

    return {
      value: id,
      label: `v${version} (${versionStatusLabel})`,
      badge,
    };
  });

  const selectedOption = selectOptions.find((option) => option.value === selectedVersion?.id);

  const refreshTouchpointVersions = useCallback(async () => {
    const results = await refetchTouchpointVersions();
    return results.data?.items ?? [];
  }, [refetchTouchpointVersions]);

  useEffect(() => {
    if (selectedVersion || previewVersionIdFromUrlRef.current) {
      return;
    }

    let cancelled = false;

    const fetchLatestVersion = async () => {
      try {
        const versions = await refreshTouchpointVersions();
        if (cancelled || previewVersionIdFromUrlRef.current) {
          return;
        }
        if (versions.length) {
          setSelectedVersion(versions[0]);
        }
      } catch (error) {
        console.error("Failed to fetch the latest touchpoint version:", error);
      }
    };

    fetchLatestVersion();
    return () => {
      cancelled = true;
    };
  }, [refreshTouchpointVersions, setSelectedVersion, selectedVersion, currentTouchpoint.id]);

  const handleArchiveVersion = (touchpoint?: TouchpointVersionAttributes) => {
    if (touchpoint) {
      const touchpointType = getTouchpointType(touchpoint);
      const formattedTouchpointType = getDisplayTouchpointType(touchpointType);

      patchStatusMutation
        .mutateAsync({ id: touchpoint.id, status: TouchpointStatuses.ARCHIVED_DRAFT })
        .then(() => {
          refreshTouchpointVersions();
          toast.success({
            data: {
              title: `Archived ${formattedTouchpointType} version ${touchpoint.version}`,
            },
          });
        })
        .catch((err) => {
          toast.error({
            data: {
              title: `Failed to archive ${getActionTouchpointTypeLabel(touchpointType).toLowerCase()}`,
              message: getErrorMessage(err?.response?.data),
            },
          });
        });
    }
    confirmArchiveModal.onClose();
  };

  const handleConfirmUpdateMergeTags = () =>
    updateMergeTags(selectedVersion?.id as string).then((newDraft) => {
      setSelectedVersion(newDraft);
      setIsLockedByAnotherUser(false);
      confirmUpdateMergeTagsModal.onClose();
      refreshTouchpointVersions();
    });

  const handleSubmitEditVersion = ({ id, description, fiscalYear }: SubmitFormProps) => {
    if (id) {
      return editVersionMutation
        .mutateAsync({
          id,
          attributes: { description, fiscalYear } as Record<string, unknown>,
        })
        .then(() => {
          toast.success({
            data: {
              title: "Updated version details",
            },
          });
          refreshTouchpointVersions();
        })
        .catch((err) => {
          toast.error({
            data: {
              title: "Failed to edit version",
              message: getErrorMessage(err?.response?.data),
            },
          });
        });
    }
  };

  const handleSubmitNewDraft = ({
    id,
    description,
    applyClientTheme,
    fiscalYear,
  }: SubmitFormProps) => {
    if (id) {
      return createDraftMutation
        .mutateAsync({
          id,
          data: {
            description,
            applyClientTheme: applyClientTheme!,
            fiscalYear,
          } as unknown as Parameters<typeof createDraftMutation.mutateAsync>[0]["data"],
        })
        .then(() => {
          toast.success({
            data: {
              title: "Created draft",
            },
          });
          return refreshTouchpointVersions();
        })
        .then((refreshedVersions) => {
          if (refreshedVersions.length > 0) {
            // The new draft will be the newest version
            setSelectedVersion(refreshedVersions[0]);
          }
          setIsLockedByAnotherUser(false);
        })
        .catch((err) => {
          toast.error({
            data: {
              title: "Failed to create draft",
              message: getErrorMessage(err?.response?.data),
            },
          });
        });
    }
  };

  const handleTouchpointStatusSending = (newStatus: string) => {
    const { CLIENT_REVIEW, REJECTED, APPROVED, PUBLISHED } = TouchpointStatuses;

    if (newStatus === REJECTED || newStatus === CLIENT_REVIEW) {
      setIsSendBtnLoading(true);
    }
    if (newStatus === APPROVED || newStatus === PUBLISHED) {
      setApprovePublishLoading(true);
    }
    return patchStatusMutation
      .mutateAsync({
        id: selectedVersion?.id as string,
        status: newStatus as Parameters<typeof patchStatusMutation.mutateAsync>[0]["status"],
      })
      .then(async () => {
        await refreshTouchpointVersions();

        if (selectedVersion) {
          setSelectedVersion({
            ...selectedVersion,
            status: newStatus,
          });
        }

        if (newStatus === APPROVED || newStatus === REJECTED) {
          navigate(-1);
        } else {
          toast.success({
            data: {
              title: getSuccessMessage(newStatus),
            },
          });
        }

        confirmPublishModal.onClose();
      })
      .catch((err) => {
        const failMessage = getFailMessage(newStatus);
        toast.error({
          data: {
            title: failMessage,
            message: getErrorMessage(err?.response?.data),
          },
        });
      })
      .finally(() => {
        setIsSendBtnLoading(false);
        setApprovePublishLoading(false);
      });
  };

  const clientVersionStatuses = getClientVersionStatuses(selectedVersion?.status);

  const canApproveSendFeedback = canApproveSendFeedbackOnTouchpoint(
    currentUser,
    clientVersionStatuses?.status,
  );

  const isApproved = getIsApproved(clientVersionStatuses?.status);
  const isPublishedTemplate = isTemplate && selectedVersionIsPublished;
  const isClientApproved = isApproved || isPublishedTemplate;

  const isUserCanPublish = getUserCanPublish(currentUser);
  const canPublishThisVersion = isUserCanPublish && !touchpointIsOther;

  const {
    isBlockedByLock,
    isBlockedByInvalidFields,
    isBlockedByOpenActionItems,
    isPublishDisabled,
  } = getTouchpointActionGates({
    selectedVersion,
    isLockedByAnotherUser,
    isTemplate,
    isApproved,
    actionItemCount: commentsSummary.actionItemCount,
  });

  const isClientReviewStatusIndicatorShown =
    !isClientAdmin &&
    !isClientUser &&
    !!clientVersionStatuses?.clientReviewStatusName &&
    selectedVersion?.version === versions[0]?.version;

  const showActionButton =
    userCanWrite &&
    (!!isVersionEditable(clientVersionStatuses) || isPublishedTemplate) &&
    isClientReviewStatusIndicatorShown;

  const isDraftOrSystemDraft = [TouchpointStatuses.DRAFT, TouchpointStatuses.SYSTEM_DRAFT].includes(
    selectedVersion?.status as TouchpointStatuses,
  );

  const isVersionInBatch = !!selectedVersion?.touchpointBatchId;
  const showAddToApprovalBatch =
    isBatchManagementEnabled && userCanWrite && isDraftOrSystemDraft && !isVersionInBatch;

  const shouldShowControlBar =
    showActionButton ||
    showAddToApprovalBatch ||
    (!isClientAdmin && !isClientUser) ||
    (isClientAdmin && canApproveSendFeedback);

  const handleOpenPublishModal = () => {
    verifyLockAndProceed(() => confirmPublishModal.onOpen());
  };

  const handleCreateDraftFromVersion = useCallback(() => {
    verifyLockAndProceed(() => newDraftModal.onOpen());
  }, [verifyLockAndProceed, newDraftModal]);

  const handleOpenContentSettings = useCallback(() => {
    verifyLockAndProceed(() => handleEditorOpen("content-settings"));
  }, [verifyLockAndProceed, handleEditorOpen]);

  return (
    <div className="flex items-center justify-between w-full mb-5" data-testid="preview-tab-header">
      <div className="flex items-start py-1">
        <div className="flex items-center gap-2">
          <Select
            selectName="version-select"
            disableClearable
            onChange={handleCustomSelectChange}
            options={selectOptions}
            value={selectedOption}
            endIcon={selectedOption?.badge}
            formatOptionLabel={renderVersionOption}
            sx={VERSION_SELECT_SX}
            ariaLabel="Select creative version"
          />
        </div>
        {versions.length > 1 && !hasClientPortalAccess && (
          <IconButton
            aria-label="Compare versions"
            variant="outlined"
            size="large"
            onClick={() => setIsCompareVersionsModalOpen(true)}
            sx={COMPARE_VERSIONS_BUTTON_SX}>
            <Icons.Compare />
          </IconButton>
        )}
        {isClientReviewStatusIndicatorShown && (
          <div className="pl-5">
            <ClientReviewStatusIndicator versionStatuses={clientVersionStatuses} />
          </div>
        )}
        {touchpointIsEmail && selectedVersion && !hasClientPortalAccess && (
          <div className="pl-5">
            <ThemeStatusIndicator applyClientTheme={selectedVersion.applyClientTheme} />
          </div>
        )}
      </div>
      <div className="flex items-center gap-4">
        {showClientUpdatesInProgressBadge && (
          <div className="flex items-center gap-1 text-sm text-aqua-700">
            <Icons.InfoIndicator className="size-4 text-aqua-600" />
            <span>Updates in progress. Our team is still preparing this creative.</span>
          </div>
        )}
        {shouldShowControlBar && (
          <ControlBar variant="inset">
            {showActionButton && (
              <>
                <DisabledActionTooltip
                  isBlockedByLock={isBlockedByLock}
                  isBlockedByInvalidFields={
                    canPublishThisVersion && isApproved && isBlockedByInvalidFields
                  }
                  isBlockedByOpenActionItems={canPublishThisVersion && isBlockedByOpenActionItems}
                  selectedVersion={selectedVersion}
                  handleOpenContentSettings={handleOpenContentSettings}
                  setIsActionsMenuOpen={setIsActionsMenuOpen}
                />
                {isApproved && !isTemplate && canPublishThisVersion && (
                  <Button
                    size="large"
                    disabled={isPublishDisabled}
                    onClick={handleOpenPublishModal}>
                    Publish
                  </Button>
                )}
                {isClientApproved && isTemplate && isUserCanPublish && (
                  <Button
                    variant="outlined"
                    size="large"
                    disabled={isBlockedByLock}
                    onClick={handleCreateDraftFromVersion}>
                    Create draft
                  </Button>
                )}
                {isApproved && (isTemplate ? !isUserCanPublish : !canPublishThisVersion) && (
                  <Button
                    variant={isTemplate ? "outlined" : undefined}
                    size="large"
                    disabled={isBlockedByLock}
                    onClick={handleEditEmail}>
                    Edit
                  </Button>
                )}
              </>
            )}

            {showAddToApprovalBatch && (
              <Button
                size="large"
                variant="contained"
                onClick={() => setIsAddToBatchModalOpen(true)}>
                Add to approval batch
              </Button>
            )}

            {!isClientAdmin && !isClientUser && (
              <PreviewTabHeaderMenu
                isActionsMenuOpen={isActionsMenuOpen}
                setIsActionsMenuOpen={setIsActionsMenuOpen}
                selectedVersion={selectedVersion}
                setSelectedVersion={setSelectedVersion}
                confirmPublishModal={confirmPublishModal}
                approveAsClientModal={approveAsClientModal}
                setIsRejectAsClientModalOpen={setIsRejectAsClientModalOpen}
                cloneTouchpointModal={cloneTouchpointModal}
                setIsCloneTemplateOpen={setIsCloneTemplateOpen}
                setIsAddToBatchModalOpen={setIsAddToBatchModalOpen}
                setIsEditProgramTemplateNameModalOpen={setIsEditProgramTemplateNameModalOpen}
                editVersionModal={editVersionModal}
                confirmArchiveModal={confirmArchiveModal}
                confirmUpdateMergeTagsModal={confirmUpdateMergeTagsModal}
                isUpdatingMergeTags={isUpdatingMergeTags}
                setIsCreateParentTouchpointModalOpen={setIsCreateParentTouchpointModalOpen}
                handleTouchpointStatusSending={handleTouchpointStatusSending}
                verifyLockAndProceed={verifyLockAndProceed}
                handleCreateDraftFromVersion={handleCreateDraftFromVersion}
                selectedVersionIsPublished={selectedVersionIsPublished}
                touchpointIsEmail={touchpointIsEmail}
                isLockedByAnotherUser={isLockedByAnotherUser}
                isApproved={isApproved}
                isClientAdmin={isClientAdmin}
                isClientUser={isClientUser}
                isUpdateClientThemeModalOpen={isUpdateClientThemeModalOpen}
                setIsUpdateClientThemeModalOpen={setIsUpdateClientThemeModalOpen}
                setIsRefineEmailContentModalOpen={setIsRefineEmailContentModalOpen}
                setCreateTouchpointFromTemplateType={setCreateTouchpointFromTemplateType}
                actionItemCount={commentsSummary.actionItemCount}
              />
            )}

            {isClientAdmin && canApproveSendFeedback && (
              <div className="flex flex-nowrap items-center gap-1">
                <TouchpointSendFeedbackButton
                  isLoading={isSendBtnLoading}
                  onSendFeedback={(status: string) => handleTouchpointStatusSending(status)}
                />
                <TouchpointApproveButton
                  isLoading={approvePublishLoading}
                  onApprove={(status: string) => handleTouchpointStatusSending(status)}
                />
              </div>
            )}
          </ControlBar>
        )}
      </div>

      {/* End Header / Begin Modals */}

      <VersionEditModal
        {...editVersionModal}
        id={selectedVersion?.id}
        isEmailType={touchpointIsEmail}
        placeholderNotes={selectedVersion?.description}
        parentName={currentCampaign.name}
        formType={FormType.EDIT}
        applyClientTheme={selectedVersion?.applyClientTheme}
        fiscalYear={selectedVersion?.fiscalYear}
        copyCode={currentTouchpoint?.copyCode}
        name={selectedVersion?.name ? selectedVersion.name : currentTouchpoint.name}
        onSubmit={handleSubmitEditVersion}
        saveText="Save"
        touchpointId={currentTouchpoint.id}
        isTemplate={isTemplate}
      />

      <VersionEditModal
        {...newDraftModal}
        id={selectedVersion?.id}
        isEmailType={touchpointIsEmail}
        parentName={currentCampaign.name}
        formType={FormType.NEW_VERSION}
        name={selectedVersion?.name ? selectedVersion.name : currentTouchpoint.name}
        onSubmit={handleSubmitNewDraft}
        fiscalYear={selectedVersion?.fiscalYear}
        copyCode={currentTouchpoint?.copyCode}
        touchpointId={currentTouchpoint.id}
        isTemplate={isTemplate}
      />

      <ConfirmArchiveModal
        {...confirmArchiveModal}
        currentTouchpoint={selectedVersion}
        onSubmit={() => handleArchiveVersion(selectedVersion)}
      />

      <ConfirmUpdateMergeTagsModal
        {...confirmUpdateMergeTagsModal}
        onSubmit={handleConfirmUpdateMergeTags}
      />

      <CloneTouchpointModal {...cloneTouchpointModal} selectedVersion={selectedVersion} />

      {isCloneTemplateOpen && selectedVersion && (
        <CloneProgramTemplatesModal
          isOpen={isCloneTemplateOpen}
          onClose={() => setIsCloneTemplateOpen(false)}
          selectedTemplates={
            [
              {
                id: currentTouchpoint.id,
                name: currentTouchpoint.name ?? "",
                type: currentTouchpoint.type ?? "",
                latestVersion: {
                  version: selectedVersion.version ?? 0,
                  status: selectedVersion.status ?? "",
                  id: selectedVersion.id ?? "",
                  applyClientTheme: selectedVersion.applyClientTheme ?? false,
                },
              },
            ] as DisplayedProgramTemplate[]
          }
        />
      )}

      {isCreateParentTouchpointModalOpen && (
        <CreateParentTouchpointModal
          setIsOpen={setIsCreateParentTouchpointModalOpen}
          isOpen={isCreateParentTouchpointModalOpen}
          selectedVersion={selectedVersion}
        />
      )}

      <ConfirmationDialog
        open={confirmPublishModal.isOpen}
        onClose={confirmPublishModal.onClose}
        onConfirm={() => handleTouchpointStatusSending(TouchpointStatuses.PUBLISHED)}
        headline={
          touchpointIsEmail
            ? "Are you sure you want to publish this email to Iterable?"
            : "Are you sure you want to publish this landing page?"
        }
        message="This will replace the current published version. This action cannot be undone."
        confirmButtonText={touchpointIsEmail ? "Publish to Iterable" : "Publish"}
      />

      <ApproveAsClientModal
        {...approveAsClientModal}
        selectedVersion={selectedVersion}
        setSelectedVersion={setSelectedVersion}
      />

      {isRejectAsClientModalOpen && (
        <RejectAsClientModal
          setIsOpen={setIsRejectAsClientModalOpen}
          selectedVersion={selectedVersion}
          setSelectedVersion={setSelectedVersion}
        />
      )}

      {isUpdateClientThemeModalOpen && (
        <UpdateClientThemeModal
          setIsOpen={setIsUpdateClientThemeModalOpen}
          isOpen={isUpdateClientThemeModalOpen}
          touchpointVersionId={selectedVersion?.id || ""}
          clientId={currentClient.id}
          campaignId={currentCampaign.id}
          touchpointId={currentTouchpoint.id}
        />
      )}

      {isRefineEmailContentModalOpen && (
        <RefineEmailContentModal
          onCloseCallback={() => setIsRefineEmailContentModalOpen(false)}
          onConfirmCallback={refineEmail}
          isSubmitting={isRefineEmailSubmitting}
          {...rewriteStrategyLinks}
        />
      )}

      {createTouchpointFromTemplateType && (
        <CreateTouchpointFromProgramTemplateModal
          isOpen={!!createTouchpointFromTemplateType}
          onClose={() => setCreateTouchpointFromTemplateType(null)}
          selectedTemplates={[
            {
              id: currentTouchpoint.id,
              name: currentTouchpoint.name,
              type: currentTouchpoint.type,
              latestVersion: selectedVersion
                ? {
                  version: selectedVersion.version ?? 0,
                  status: selectedVersion.status ?? "",
                  id: selectedVersion.id ?? "",
                  applyClientTheme: selectedVersion.applyClientTheme ?? false,
                }
                : null,
            },
          ]}
        />
      )}

      {isCompareVersionsModalOpen && (
        <CompareVersionsModal
          setIsOpen={setIsCompareVersionsModalOpen}
          isOpen={isCompareVersionsModalOpen}
          touchpointVersionId={selectedVersion?.id || ""}
          hideSubjectAndPreheader={isTemplate}
        />
      )}

      {isEditProgramTemplateNameModalOpen && (
        <EditProgramTemplateNameModal
          setIsOpen={setIsEditProgramTemplateNameModalOpen}
          isOpen={isEditProgramTemplateNameModalOpen}
        />
      )}
    </div>
  );
};
